import { ref, computed, getCurrentInstance } from '@vue/composition-api'
import { format } from 'date-fns'
import { useKitchenSlipPrint } from '@yonarezi/yonarezi3.slip-print'
import KitchenPrinterApi from '@/api/waiter/KitchenPrinter'
import { removePortFromIpAddress } from '@/utils/removePortFromIpAddress'
import { printerConnectionPool } from '@/utils/printerConnectionPool'
import { reportPrinterError, wrapPrinterReceive } from '@/utils/sentryReportPrinterError'
import useCurrentData from '@/views/composable/useCurrentData'
import useKitchenPrinterSelection from '@/views/composable/useKitchenPrinterSelection'

/**
 * VendingHistoryから受領者名の配列を抽出する
 * ActionCable形式とJSONAPI形式の両方に対応
 */
function extractRecipientNames(vh) {
  // ActionCable形式: vh.recipients = [{ name: '...' }]
  if (vh.recipients) {
    return vh.recipients.map(r => r.name).filter(Boolean)
  }

  // JSONAPI形式: vh.attributes.vendibleRecipients.data[].attributes.recipient.data.attributes.name
  const recipientsData = vh.attributes?.vendibleRecipients?.data
  if (recipientsData && recipientsData.length > 0) {
    return recipientsData
      .map(vr => vr.attributes?.recipient?.data?.attributes?.name)
      .filter(Boolean)
  }

  return []
}

/**
 * オプション配列を表示用テキストに変換する
 */
function formatOptionsText(selectedOptions) {
  return (selectedOptions || [])
    .map(o => (o.value != null ? `${o.group_name}: ${o.value}` : o.option_name))
    .filter(Boolean)
    .join(' / ')
}

export default function useKitchenPrint() {
  const vm = getCurrentInstance().proxy
  const kitchenPrinters = ref([])
  const isLoaded = ref(false)
  const { isPrinterEnabled } = useKitchenPrinterSelection()
  const { currentClub } = useCurrentData()

  const fetchKitchenPrinters = async () => {
    if (isLoaded.value) return

    const res = await KitchenPrinterApi.getKitchenPrinters()
    if (res?.status === 200) {
      kitchenPrinters.value = res.data.kitchenPrinters.data
      isLoaded.value = true
    }
  }

  // vendible_tag_id → kitchen_printer のマッピングを構築
  const tagToPrinterMap = computed(() => {
    const map = {}
    kitchenPrinters.value.forEach(printer => {
      const tagIds = printer.attributes.routingVendibleTagIds || []
      tagIds.forEach(tagId => {
        map[tagId] = printer
      })
    })

    return map
  })

  /**
   * 注文データからキッチン伝票を印刷する
   * プリンター振り分け後、商品ごとにグループ化して1プリンター×1商品＝1伝票
   * @param {Object} params
   * @param {Array} params.vendingHistories - VendingHistoryのリスト
   * @param {string} params.staffName - 担当者名
   * @param {string} params.hallName - ホール名
   * @param {string} params.tableCode - 卓コード
   * @param {number} params.customerCount - 客数
   */
  const printKitchenSlips = async ({
    vendingHistories,
    staffName,
    hallName,
    tableCode,
    customerCount,
  }) => {
    await fetchKitchenPrinters()

    if (kitchenPrinters.value.length === 0) return

    // printerId + itemName → { printer, itemName, quantity, remark, recipientEntries }
    const printerItemGroups = {}

    const addToGroup = (printer, itemName, remark, recipientNames, selectedOptions) => {
      const groupKey = `${printer.id}_${itemName}`
      if (!printerItemGroups[groupKey]) {
        printerItemGroups[groupKey] = {
          printer,
          itemName,
          quantity: 0,
          remark: remark || undefined,
          // 受領者名とオプションのペアを保持
          recipientEntries: [],
        }
      }

      printerItemGroups[groupKey].quantity += 1

      const optionsText = formatOptionsText(selectedOptions)

      // 受領者名（オプション付き）を重複なく収集
      if (recipientNames.length > 0) {
        recipientNames.forEach(name => {
          const existing = printerItemGroups[groupKey].recipientEntries.find(e => e.name === name && e.optionsText === optionsText)
          if (!existing) {
            printerItemGroups[groupKey].recipientEntries.push({ name, optionsText })
          }
        })
      } else {
        // 受領者なし（ゲスト注文）: 「ゲスト」として記録
        const existing = printerItemGroups[groupKey].recipientEntries.find(e => e.name === 'ゲスト' && e.optionsText === optionsText)
        if (!existing) {
          printerItemGroups[groupKey].recipientEntries.push({ name: 'ゲスト', optionsText })
        }
      }
    }

    vendingHistories.forEach(vh => {
      // ActionCable形式: vh.vendibleTagIds = [1, 2, ...]
      // JSONAPI形式: vh.attributes.vendibleTags.data[].id
      const tagIds = vh.vendibleTagIds
        || vh.attributes?.vendibleTagIds
        || vh.attributes?.vendibleTags?.data?.map(t => Number(t.id))
        || []
      const itemName = vh.name || vh.attributes?.vendibleName || vh.attributes?.name
      const remark = vh.remark || vh.attributes?.remark
      const recipientNames = extractRecipientNames(vh)
      const selectedOptions = vh.selected_options || vh.selectedOptions || vh.attributes?.selectedOptions || []

      // タグからプリンターを特定し、重複を除去（1VH = 各プリンターに1回）
      const printersForVh = new Map()
      tagIds.forEach(tagId => {
        const printer = tagToPrinterMap.value[tagId]
        if (!printer) return
        if (!isPrinterEnabled(printer.id)) return
        printersForVh.set(printer.id, printer)
      })

      printersForVh.forEach(printer => {
        addToGroup(printer, itemName, remark, recipientNames, selectedOptions)
      })
    })

    // 同じプリンターIP宛の印刷を順次実行（ePOS SDKの同時接続制限対策）
    // 異なるIPのプリンターは並列で実行
    const orderedAt = format(new Date(), 'HH:mm')
    const groups = Object.values(printerItemGroups)

    // プリンターIP別にグループ化
    const byIp = {}
    groups.forEach(group => {
      const ipAddress = removePortFromIpAddress(group.printer.attributes.ipAddress)
      if (!byIp[ipAddress]) byIp[ipAddress] = []
      byIp[ipAddress].push(group)
    })

    // 同じIP内は順次実行（reduceでPromiseチェーン）、異なるIPは並列実行
    const sendOne = async (ipAddress, {
      printer, itemName, quantity, remark, recipientEntries,
    }) => {
      // 受領者名にオプション情報を付加
      // 全員同じオプションの場合はremarkに表示し、異なる場合は名前の横に表示
      const uniqueOptionsTexts = [...new Set(recipientEntries.map(e => e.optionsText))]
      const allSameOptions = uniqueOptionsTexts.length <= 1

      let recipientNames
      let combinedRemark

      if (allSameOptions) {
        // 全員同じオプション（または受領者なし）: 従来通りremarkに表示
        recipientNames = recipientEntries.map(e => e.name).filter(Boolean)
        const sharedOptionsText = uniqueOptionsTexts[0] || ''
        combinedRemark = [sharedOptionsText, remark].filter(Boolean).join(' | ') || undefined
      } else {
        // 受領者ごとに異なるオプション: 名前の横に表示
        recipientNames = recipientEntries
          .filter(e => e.name)
          .map(e => (e.optionsText ? `${e.name}: ${e.optionsText}` : e.name))
        combinedRemark = remark || undefined
      }

      // 接続プールから接続済みプリンターを取得（キャッシュヒット時はSSLハンドシェイクなし）
      let cachedPrinter
      try {
        cachedPrinter = await printerConnectionPool.getConnection(ipAddress)
      } catch (error) {
        reportPrinterError(error, {
          kind: 'kitchen',
          phase: 'connect',
          ipAddress,
          clubId: currentClub.value?.id,
          printerName: printer.attributes?.name,
        })
        vm.$toast.error(`キッチンプリンター（${printer.attributes.name}）への接続に失敗しました`)

        return Promise.resolve()
      }

      const options = computed(() => ({
        ipAddress,
        printer: cachedPrinter,
        orderedAt,
        staffName: staffName || '',
        hallName: hallName || '',
        tableCode: tableCode || '',
        customerCount: customerCount || 0,
        itemName,
        itemQuantity: quantity,
        itemRemark: combinedRemark,
        recipientNames,
      }))

      return new Promise(resolve => {
        const { send, onReceive } = useKitchenSlipPrint(options)
        onReceive.value = wrapPrinterReceive(
          () => resolve(),
          {
            kind: 'kitchen',
            ipAddress,
            clubId: currentClub.value?.id,
            printerName: `キッチンプリンター（${printer.attributes?.name ?? ''}）`,
            toast: vm.$toast,
          },
        )
        send().catch(error => {
          reportPrinterError(error, {
            kind: 'kitchen',
            phase: 'print',
            ipAddress,
            clubId: currentClub.value?.id,
            printerName: printer.attributes?.name,
          })
          vm.$toast.error(`キッチンプリンター（${printer.attributes.name}）への印刷に失敗しました`)
          resolve()
        })
      })
    }

    const ipPromises = Object.entries(byIp).map(([ipAddress, ipGroups]) => {
      return ipGroups.reduce(
        (chain, group) => chain.then(() => sendOne(ipAddress, group)),
        Promise.resolve(),
      )
    })

    await Promise.allSettled(ipPromises)
  }

  return {
    kitchenPrinters,
    fetchKitchenPrinters,
    printKitchenSlips,
  }
}
